// 사진 촬영 시각 읽기. 사진을 줄여 다시 저장하면 파일 안의 촬영 정보가 지워지므로 줄이기 전에 읽는다.
// 서버의 등록 시각은 올라간 시각이라, 지하주차장에서 찍고 지상에서 올리면 실제 찍은 시각과 다르다.

/** 서버 PhotoTakenAtSource 와 같은 값 */
export type TakenAtSource = "EXIF" | "FILE";

export interface CaptureTime {
  /** "yyyy-MM-dd HH:mm:ss" (휴대폰 현지 시각). 서버의 다른 날짜 필드와 같은 형식 */
  takenAt: string;
  source: TakenAtSource;
}

// EXIF 는 파일 앞부분(APP1)에 있다. 큰 사진 전체를 읽지 않도록 앞부분만 본다.
const EXIF_SCAN_BYTES = 256 * 1024;

/**
 * 촬영 시각을 읽는다.
 * 1) JPEG 안의 EXIF 촬영 시각(DateTimeOriginal, 없으면 DateTime)
 * 2) 없으면 파일 수정 시각. 카메라로 바로 찍은 파일이면 찍은 시각에 가깝지만,
 *    앨범에서 고르면 고른 시각일 수 있어 출처를 FILE 로 남긴다
 * 둘 다 없으면 null.
 */
export async function readCaptureTime(file: File): Promise<CaptureTime | null> {
  try {
    const exif = await readExifDateTime(file);
    if (exif) return { takenAt: exif, source: "EXIF" };
  } catch {
    // 읽지 못하면 파일 시각으로 넘어간다
  }
  if (file.lastModified > 0) {
    return { takenAt: formatLocal(new Date(file.lastModified)), source: "FILE" };
  }
  return null;
}

/** JPEG 의 EXIF 촬영 시각을 "yyyy-MM-dd HH:mm:ss" 로. JPEG 가 아니거나 없으면 null */
export async function readExifDateTime(file: Blob): Promise<string | null> {
  const buffer = await readAsArrayBuffer(file.slice(0, EXIF_SCAN_BYTES));
  const view = new DataView(buffer);
  try {
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;
    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset);
      if ((marker & 0xff00) !== 0xff00) return null;
      // SOS 이후는 이미지 데이터라 EXIF 가 없다
      if (marker === 0xffda) return null;
      const size = view.getUint16(offset + 2);
      // APP1 + "Exif\0\0"
      if (
        marker === 0xffe1 &&
        offset + 10 <= view.byteLength &&
        view.getUint32(offset + 4) === 0x45786966 &&
        view.getUint16(offset + 8) === 0
      ) {
        return parseTiffDateTime(view, offset + 10);
      }
      offset += 2 + size;
    }
    return null;
  } catch {
    // 손상된 파일이면 범위를 벗어난다
    return null;
  }
}

const TAG_EXIF_IFD = 0x8769;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME = 0x0132;

function parseTiffDateTime(view: DataView, tiffStart: number): string | null {
  const littleEndian = view.getUint16(tiffStart) === 0x4949;
  const u16 = (at: number) => view.getUint16(at, littleEndian);
  const u32 = (at: number) => view.getUint32(at, littleEndian);
  if (u16(tiffStart + 2) !== 0x002a) return null;

  const findEntry = (ifdStart: number, tag: number): number | null => {
    const count = u16(ifdStart);
    for (let i = 0; i < count; i++) {
      const entry = ifdStart + 2 + i * 12;
      if (u16(entry) === tag) return entry;
    }
    return null;
  };
  const readAscii = (entry: number): string => {
    const length = u32(entry + 4);
    const start = length > 4 ? tiffStart + u32(entry + 8) : entry + 8;
    let text = "";
    for (let i = 0; i < length; i++) {
      const code = view.getUint8(start + i);
      if (code === 0) break;
      text += String.fromCharCode(code);
    }
    return text;
  };

  const ifd0 = tiffStart + u32(tiffStart + 4);
  const exifPointer = findEntry(ifd0, TAG_EXIF_IFD);
  if (exifPointer !== null) {
    const exifIfd = tiffStart + u32(exifPointer + 8);
    const original = findEntry(exifIfd, TAG_DATE_TIME_ORIGINAL);
    const value = original !== null ? toServerFormat(readAscii(original)) : null;
    if (value) return value;
  }
  const dateTime = findEntry(ifd0, TAG_DATE_TIME);
  return dateTime !== null ? toServerFormat(readAscii(dateTime)) : null;
}

/** EXIF "2026:10:01 09:50:12" → "2026-10-01 09:50:12". 형식이 아니거나 비어 있으면 null */
export function toServerFormat(exifValue: string): string | null {
  const match = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(exifValue.trim());
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  if (year === "0000") return null;
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** 기기 현지 시각을 "yyyy-MM-dd HH:mm:ss" 로 */
export function formatLocal(date: Date): string {
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

// Blob.arrayBuffer() 가 없는 구형 Safari 를 위해 FileReader 로 읽는다
function readAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/** 서버로 보낼 촬영 시각 칸. 읽지 못했으면 빈 객체 (칸을 보내지 않는다) */
export function captureFields(
  capture: CaptureTime | null | undefined
): { takenAt?: string; takenAtSource?: TakenAtSource } {
  return capture ? { takenAt: capture.takenAt, takenAtSource: capture.source } : {};
}
